import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useState, useEffect, createContext, useMemo, useCallback } from "react";
import Home from "@/pages/Home";
import Marketplace from "@/pages/Marketplace";
import ListingDetail from "@/pages/ListingDetail";
import Consultants from "@/pages/Consultants";
import Franchises from "@/pages/Franchises";
import Dashboard from "@/pages/Dashboard";
import Auth from "@/pages/Auth";
import ForgotPassword from "@/pages/ForgotPassword";
import ListBusiness from "@/pages/ListBusiness";
import Messages from "@/pages/Messages";
import AdminPanel from "@/pages/AdminPanel";
import AboutUs from "@/pages/AboutUs";
import ContactUs from "@/pages/ContactUs";
import BusinessPolicies from "@/pages/BusinessPolicies";
import PostRequirement from "@/pages/PostRequirement";
import Profile from "@/pages/Profile";
import Deals from "@/pages/Deals";
import DealDetail from "@/pages/DealDetail";
import Rentals from "@/pages/Rentals";
import RentalDetail from "@/pages/RentalDetail";
import ListRental from "@/pages/ListRental";
import Valuation from "@/pages/Valuation";
import DueDiligence from "@/pages/DueDiligence";
import SaleAgreement from "@/pages/SaleAgreement";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import ChatWidget from "@/components/ChatWidget";
import { PushNotificationProvider } from "@/contexts/PushNotificationContext";
// import BusinessReview from "@/pages/BusinessReview";
import "@/App.css";

export const AuthContext = createContext(null);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userData = localStorage.getItem("user");
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  const login = useCallback((token, userData) => {
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        localStorage.setItem("user", JSON.stringify(userData));
        setUser(userData);
      }
    } catch (error) {
      console.error("Failed to refresh user:", error);
    }
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const authContextValue = useMemo(() => ({ 
    user, login, logout, refreshUser 
  }), [user, login, logout, refreshUser]);

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return (
    <AuthContext.Provider value={authContextValue}>
      <PushNotificationProvider user={user}>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/marketplace" element={<Marketplace />} />
            <Route path="/listing/:id" element={<ListingDetail />} />
            <Route path="/consultants" element={<Consultants />} />
            <Route path="/franchises" element={<Franchises />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/policies" element={<BusinessPolicies />} />
            {/* <Route path="/business-review" element={<BusinessReview />} /> */}
            <Route path="/auth" element={<Auth />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route
              path="/dashboard"
              element={user ? <Dashboard /> : <Navigate to="/auth" />}
            />
            <Route
              path="/list-business"
              element={user ? <ListBusiness /> : <Navigate to="/auth" />}
            />
            <Route
              path="/messages"
              element={user ? <Messages /> : <Navigate to="/auth" />}
            />
            <Route
              path="/admin"
              element={user?.role === "admin" ? <AdminPanel /> : <Navigate to="/" />}
            />
            <Route
              path="/post-requirement"
              element={user ? <PostRequirement /> : <Navigate to="/auth" />}
            />
            <Route
              path="/profile"
              element={user ? <Profile /> : <Navigate to="/auth" />}
            />
            <Route
              path="/deals"
              element={user ? <Deals /> : <Navigate to="/auth" />}
            />
            <Route
              path="/deals/:dealId"
              element={user ? <DealDetail /> : <Navigate to="/auth" />}
            />
            <Route path="/rentals" element={<Rentals />} />
            <Route path="/rentals/:rentalId" element={<RentalDetail />} />
            <Route
              path="/list-rental"
              element={user ? <ListRental /> : <Navigate to="/auth" />}
            />
            <Route path="/valuation" element={<Valuation />} />
            <Route path="/due-diligence" element={<DueDiligence />} />
            <Route path="/sale-agreement" element={<SaleAgreement />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
          </Routes>
          <ChatWidget />
          <Toaster position="top-right" />
        </BrowserRouter>
      </PushNotificationProvider>
    </AuthContext.Provider>
  );
}

export default App;